import type { IEmailFilters } from '../EmailTriggerDialog';
import type { TConfigurationValues } from '../WebhookTriggerDialog';
import {
  type Bdkv2Expression,
  type Bdkv2Value,
  type LibraryServiceAddWorkspaceBookConnectionPollInstanceBodyConfiguration,
  type LibraryServiceAddWorkspaceBookConnectionTriggerInstanceBodyConfiguration,
  type V2PollInstanceConfiguration,
  type V2TriggerInstanceConfiguration,
  Bdkv2BinaryOperator,
  Bdkv2UnaryOperator,
} from '@/generated/hooks/bdk/models';

/**
 * Email trigger configuration type (just email address)
 */
export type TEmailTriggerConfiguration = {
  email_address: { text_value: string };
};

/**
 * Return type for createEmailTriggerConfigurationAndFilters
 */
export type TEmailTriggerConfigurationAndFilters = {
  configuration: LibraryServiceAddWorkspaceBookConnectionTriggerInstanceBodyConfiguration;
  filter_expression?: Bdkv2Expression;
};

/**
 * Filter field mapping from IEmailFilters to filter expression field names
 */
const FILTER_FIELD_MAP: Record<keyof IEmailFilters, string> = {
  from: 'email.sender',
  cc: 'email.cc',
  subjectHasWords: 'email.subject',
  subjectDoesntHaveWords: 'email.subject',
  bodyHasWords: 'email.body',
  bodyDoesntHaveWords: 'email.body',
  hasAttachments: 'email.attachments',
};

/**
 * A single filter condition extracted from a filter expression tree.
 *
 * Shared between the email-specific converters and the generic trigger filter
 * builder so both interpret the BDK expression shape identically.
 */
export type TExtractedCondition = {
  field: string;
  value: string | boolean | number;
  operator?: Bdkv2BinaryOperator;
  isNegated?: boolean;
};

/**
 * Creates a value expression with text value
 */
function createTextValue(value: string): Bdkv2Value {
  return { text_value: value };
}

/**
 * Creates a value expression with boolean value
 */
function createBooleanValue(value: boolean): Bdkv2Value {
  return { boolean_value: value };
}

/**
 * Creates a value expression with number value
 */
function createNumberValue(value: number): Bdkv2Value {
  return { number_value: value };
}

/**
 * Creates a binary expression for a single filter condition
 */
export function createBinaryExpression(
  fieldName: string,
  operator: Bdkv2BinaryOperator,
  value: Bdkv2Value,
): Bdkv2Expression {
  return {
    binary_expression: {
      left: { value_expression: createTextValue(fieldName) },
      operator,
      right: { value_expression: value },
    },
  };
}

/**
 * Creates a unary expression with UnaryOperatorNot
 */
export function createUnaryNotExpression(
  inner: Bdkv2Expression,
): Bdkv2Expression {
  return {
    unary_expression: {
      operator: Bdkv2UnaryOperator.UnaryOperatorNot,
      inner,
    },
  };
}

/**
 * Creates a unary expression with UnaryOperatorExists
 */
export function createUnaryExistsExpression(
  fieldName: string,
): Bdkv2Expression {
  return {
    unary_expression: {
      operator: 'UnaryOperatorExists' as Bdkv2UnaryOperator,
      inner: {
        value_expression: createTextValue(fieldName),
      },
    },
  };
}

/**
 * Combines two filter expressions with AND operator
 */
export function combineWithAnd(
  left: Bdkv2Expression,
  right: Bdkv2Expression,
): Bdkv2Expression {
  return {
    binary_expression: {
      left,
      operator: Bdkv2BinaryOperator.BinaryOperatorAnd,
      right,
    },
  };
}

/**
 * Converts IEmailFilters to a filter_expression using BDK types.
 *
 * @param filters - Email filters from the dialog
 * @returns Filter expression or undefined if no filters
 */
export function convertFiltersToExpression(
  filters: IEmailFilters,
): Bdkv2Expression | undefined {
  const expressions: Bdkv2Expression[] = [];

  // From (sender) - use BinaryOperatorEquals
  if (filters.from) {
    expressions.push(
      createBinaryExpression(
        FILTER_FIELD_MAP.from,
        Bdkv2BinaryOperator.BinaryOperatorEquals,
        createTextValue(filters.from),
      ),
    );
  }

  // CC - use BinaryOperatorHas
  if (filters.cc) {
    expressions.push(
      createBinaryExpression(
        FILTER_FIELD_MAP.cc,
        Bdkv2BinaryOperator.BinaryOperatorHas,
        createTextValue(filters.cc),
      ),
    );
  }

  // Subject has words - use BinaryOperatorHas
  if (filters.subjectHasWords) {
    expressions.push(
      createBinaryExpression(
        FILTER_FIELD_MAP.subjectHasWords,
        Bdkv2BinaryOperator.BinaryOperatorHas,
        createTextValue(filters.subjectHasWords),
      ),
    );
  }

  // Subject doesn't have words - wrap BinaryOperatorHas with UnaryOperatorNot
  if (filters.subjectDoesntHaveWords) {
    const hasExpression = createBinaryExpression(
      FILTER_FIELD_MAP.subjectDoesntHaveWords,
      Bdkv2BinaryOperator.BinaryOperatorHas,
      createTextValue(filters.subjectDoesntHaveWords),
    );
    expressions.push(createUnaryNotExpression(hasExpression));
  }

  // Body has words - use BinaryOperatorHas
  if (filters.bodyHasWords) {
    expressions.push(
      createBinaryExpression(
        FILTER_FIELD_MAP.bodyHasWords,
        Bdkv2BinaryOperator.BinaryOperatorHas,
        createTextValue(filters.bodyHasWords),
      ),
    );
  }

  // Body doesn't have words - wrap BinaryOperatorHas with UnaryOperatorNot
  if (filters.bodyDoesntHaveWords) {
    const hasExpression = createBinaryExpression(
      FILTER_FIELD_MAP.bodyDoesntHaveWords,
      Bdkv2BinaryOperator.BinaryOperatorHas,
      createTextValue(filters.bodyDoesntHaveWords),
    );
    expressions.push(createUnaryNotExpression(hasExpression));
  }

  // Has attachments - use UnaryOperatorExists when true
  if (filters.hasAttachments === true) {
    expressions.push(
      createUnaryExistsExpression(FILTER_FIELD_MAP.hasAttachments),
    );
  }

  // Combine all expressions with AND
  if (expressions.length === 0) {
    return undefined;
  }

  if (expressions.length === 1) {
    return expressions[0];
  }

  // Combine multiple expressions with AND
  let combined = expressions[0];
  for (let i = 1; i < expressions.length; i++) {
    combined = combineWithAnd(combined, expressions[i]);
  }

  return combined;
}

/**
 * Extracts the field name from an expression
 */
function getFieldName(expr: Bdkv2Expression | undefined): string | undefined {
  if (expr?.value_expression?.text_value) {
    return expr.value_expression.text_value;
  }
  return undefined;
}

/**
 * Extracts the value from an expression
 */
function getValue(
  expr: Bdkv2Expression | undefined,
): string | boolean | number | undefined {
  const valExpr = expr?.value_expression;
  if (!valExpr) return undefined;

  if (valExpr.text_value !== undefined) return valExpr.text_value;
  if (valExpr.boolean_value !== undefined) return valExpr.boolean_value;
  if (valExpr.number_value !== undefined) return valExpr.number_value;

  return undefined;
}

/**
 * Recursively extracts filter conditions from a filter expression
 */
export function extractConditions(
  expr: Bdkv2Expression | undefined,
  conditions: TExtractedCondition[],
): void {
  if (!expr) return;

  // Handle unary expressions
  if (expr.unary_expression) {
    const unaryExpr = expr.unary_expression;

    if (unaryExpr.operator === Bdkv2UnaryOperator.UnaryOperatorNot) {
      // For NOT expressions, recursively extract and mark as negated
      if (unaryExpr.inner) {
        extractConditions(unaryExpr.inner, conditions);
        // Mark the last condition as negated
        if (conditions.length > 0) {
          conditions[conditions.length - 1].isNegated = true;
        }
      }
    } else if (unaryExpr.operator === 'UnaryOperatorExists') {
      // For EXISTS expressions, extract the field name
      const field = getFieldName(unaryExpr.inner);
      if (field !== undefined) {
        conditions.push({ field, value: true, isNegated: false });
      }
    }
    return;
  }

  // Handle binary expressions
  if (!expr.binary_expression) return;

  const binExpr = expr.binary_expression;

  if (binExpr.operator === Bdkv2BinaryOperator.BinaryOperatorAnd) {
    // Recursively process AND branches
    extractConditions(binExpr.left, conditions);
    extractConditions(binExpr.right, conditions);
  } else {
    // Leaf condition - extract field and value
    const field = getFieldName(binExpr.left);
    const value = getValue(binExpr.right);
    if (field !== undefined && value !== undefined) {
      conditions.push({
        field,
        value,
        operator: binExpr.operator,
        isNegated: false,
      });
    }
  }
}

/**
 * Converts a filter_expression back to IEmailFilters.
 *
 * @param expression - Filter expression from configuration
 * @returns IEmailFilters object
 */
export function convertExpressionToFilters(
  expression: Bdkv2Expression | undefined,
): IEmailFilters {
  const filters: IEmailFilters = {};

  if (!expression) {
    return filters;
  }

  // Extract all conditions from the expression tree
  const conditions: TExtractedCondition[] = [];
  extractConditions(expression, conditions);

  // Map conditions to filter fields
  for (const { field, value, operator, isNegated } of conditions) {
    // Handle email.* field names
    if (field === 'email.sender') {
      if (
        typeof value === 'string' &&
        operator === Bdkv2BinaryOperator.BinaryOperatorEquals
      ) {
        filters.from = value;
      }
    } else if (field === 'email.cc') {
      if (
        typeof value === 'string' &&
        operator === Bdkv2BinaryOperator.BinaryOperatorHas
      ) {
        filters.cc = value;
      }
    } else if (field === 'email.subject') {
      if (typeof value === 'string') {
        if (isNegated) {
          filters.subjectDoesntHaveWords = value;
        } else if (operator === Bdkv2BinaryOperator.BinaryOperatorHas) {
          filters.subjectHasWords = value;
        }
      }
    } else if (field === 'email.body') {
      if (typeof value === 'string') {
        if (isNegated) {
          filters.bodyDoesntHaveWords = value;
        } else if (operator === Bdkv2BinaryOperator.BinaryOperatorHas) {
          filters.bodyHasWords = value;
        }
      }
    } else if (field === 'email.attachments') {
      if (value === true) {
        filters.hasAttachments = true;
      }
    }
  }

  return filters;
}

/**
 * Creates email trigger configuration and filter expression.
 *
 * @param emailAddress - Generated email address
 * @param filters - Optional email filters
 * @returns Object with configuration and optional filter_expression
 */
export function createEmailTriggerConfigurationAndFilters(
  emailAddress: string,
  filters?: IEmailFilters,
): TEmailTriggerConfigurationAndFilters {
  const result: TEmailTriggerConfigurationAndFilters = {
    configuration: {
      email_address: { text_value: emailAddress },
    },
  };

  if (filters) {
    const filterExpression = convertFiltersToExpression(filters);
    if (filterExpression) {
      result.filter_expression = filterExpression;
    }
  }

  return result;
}

/**
 * Parses the email address from email trigger configuration.
 *
 * @param configuration - BDK-formatted configuration from trigger instance
 * @returns Email address string or undefined if not found
 */
export function parseEmailTriggerConfiguration(
  configuration: V2TriggerInstanceConfiguration | undefined,
): string | undefined {
  if (!configuration) {
    return undefined;
  }

  const emailValue = configuration.email_address;
  if (emailValue?.text_value) {
    return emailValue.text_value;
  }

  return undefined;
}

// ============================================================================
// Webhook Trigger Configuration Utilities
// ============================================================================

/**
 * Creates a Bdkv2Value from a primitive value (string, number, or boolean).
 *
 * @param value - Primitive value to convert
 * @returns Bdkv2Value with appropriate type field set
 */
export function createBdkValue(value: string | number | boolean): Bdkv2Value {
  if (typeof value === 'string') {
    return createTextValue(value);
  }
  if (typeof value === 'number') {
    return createNumberValue(value);
  }
  if (typeof value === 'boolean') {
    return createBooleanValue(value);
  }
  // Fallback to text for any other type
  return createTextValue(String(value));
}

/**
 * Extracts a primitive value from a Bdkv2Value.
 *
 * @param bdkValue - BDK value object
 * @returns Primitive value (string, number, or boolean) or undefined
 */
export function extractBdkValue(
  bdkValue: Bdkv2Value | undefined,
): string | number | boolean | undefined {
  if (!bdkValue) return undefined;

  if (bdkValue.text_value !== undefined) return bdkValue.text_value;
  if (bdkValue.number_value !== undefined) return bdkValue.number_value;
  if (bdkValue.boolean_value !== undefined) return bdkValue.boolean_value;

  return undefined;
}

/**
 * Creates webhook trigger configuration from TConfigurationValues.
 * Converts { key: value } to { key: { text_value/number_value/boolean_value: value } }
 *
 * @param configValues - Configuration values from the dialog (Record<string, string | number | boolean>)
 * @returns BDK-formatted configuration object
 *
 * @example
 * // Input
 * { url: "https://example.com", timeout: 30, enabled: true }
 *
 * // Output
 * {
 *   url: { text_value: "https://example.com" },
 *   timeout: { number_value: 30 },
 *   enabled: { boolean_value: true }
 * }
 */
export function createWebhookTriggerConfiguration(
  configValues: TConfigurationValues,
): LibraryServiceAddWorkspaceBookConnectionTriggerInstanceBodyConfiguration {
  const configuration: LibraryServiceAddWorkspaceBookConnectionTriggerInstanceBodyConfiguration =
    {};

  for (const [key, value] of Object.entries(configValues)) {
    if (value !== undefined && value !== null) {
      configuration[key] = createBdkValue(value);
    }
  }

  return configuration;
}

/**
 * Parses BDK configuration back to TConfigurationValues.
 * Converts { key: { text_value/number_value/boolean_value: value } } to { key: value }
 *
 * @param configuration - BDK-formatted configuration from trigger instance
 * @returns Flat configuration values (Record<string, string | number | boolean>)
 *
 * @example
 * // Input
 * {
 *   url: { text_value: "https://example.com" },
 *   timeout: { number_value: 30 },
 *   enabled: { boolean_value: true }
 * }
 *
 * // Output
 * { url: "https://example.com", timeout: 30, enabled: true }
 */
export function parseWebhookTriggerConfiguration(
  configuration: V2TriggerInstanceConfiguration | undefined,
): TConfigurationValues {
  const configValues: TConfigurationValues = {};

  if (!configuration) {
    return configValues;
  }

  for (const [key, bdkValue] of Object.entries(configuration)) {
    const value = extractBdkValue(bdkValue);
    if (value !== undefined) {
      configValues[key] = value;
    }
  }

  return configValues;
}

/**
 * Creates poll trigger configuration from TConfigurationValues.
 * Converts { key: value } to { key: { text_value/number_value/boolean_value: value } }
 *
 * @param configValues - Configuration values from the dialog (Record<string, string | number | boolean>)
 * @returns BDK-formatted configuration object
 *
 * @example
 * // Input
 * { url: "https://example.com", timeout: 30, enabled: true }
 *
 * // Output
 * {
 *   url: { text_value: "https://example.com" },
 *   timeout: { number_value: 30 },
 *   enabled: { boolean_value: true }
 * }
 */
export function createPollTriggerConfiguration(
  configValues: TConfigurationValues,
): LibraryServiceAddWorkspaceBookConnectionPollInstanceBodyConfiguration {
  const configuration: LibraryServiceAddWorkspaceBookConnectionPollInstanceBodyConfiguration =
    {};

  for (const [key, value] of Object.entries(configValues)) {
    if (value !== undefined && value !== null) {
      configuration[key] = createBdkValue(value);
    }
  }

  return configuration;
}

/**
 * Parses BDK poll instance configuration back to TConfigurationValues.
 * Mirrors parseWebhookTriggerConfiguration for the poll instance shape.
 *
 * @param configuration - BDK-formatted configuration from poll instance
 * @returns Flat configuration values (Record<string, string | number | boolean>)
 */
export function parsePollTriggerConfiguration(
  configuration: V2PollInstanceConfiguration | undefined,
): TConfigurationValues {
  const configValues: TConfigurationValues = {};

  if (!configuration) {
    return configValues;
  }

  for (const [key, bdkValue] of Object.entries(configuration)) {
    const value = extractBdkValue(bdkValue);
    if (value !== undefined) {
      configValues[key] = value;
    }
  }

  return configValues;
}
